import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { RecipientsFilter } from '../../../../generated/apollo-hooks';
import TabList from '../../TabList/TabList';
import classes from './RecipientsListModal.module.scss';
import { useTranslation } from 'react-i18next';
import useSelector from '../../../infrastructure/useSelector';
import { WalkmeFeatureFlags } from '@communication-center/data-objects';

const keyPrefix = 'components.audiences.recipientsListModal.recipientsListModalTabs';

export const useRecipientsModalTabs = (tab = Tabs.ACTIVE) => {
    const [activeTab, setActiveTab] = useState(tab);
    const tabFilter = useCallback(() => getFilterForTab(activeTab), [activeTab]);
    return { activeTab, setActiveTab, getFilterForTab: tabFilter };
};

export enum Tabs {
    ALL = 'All Recipients',
    ACTIVE = 'Active',
    INACTIVE = 'Inactive',
    POTENTIAL = 'Potential'
}

export const getFilterForTab = (tab: Tabs): RecipientsFilter => {
    switch (tab) {
        case Tabs.ACTIVE:
            return RecipientsFilter.Active;
        case Tabs.ALL:
            return RecipientsFilter.All;
        case Tabs.INACTIVE:
            return RecipientsFilter.Inactive;
        case Tabs.POTENTIAL:
            return RecipientsFilter.Potential;
    }
};

const AllTabs = [Tabs.ALL, Tabs.ACTIVE, Tabs.INACTIVE, Tabs.POTENTIAL];
export const RecipientsListModalTabs = ({
    activeTab,
    setActiveTab,
    getAllRecipientsSizeError,
    allRecipientsCount,
    setQuery,
    isLoading,
    query
}: $TSFixMe) => {
    const { t } = useTranslation('', { keyPrefix });
    const getRecipientsCount = useCallback(
        (tab: Tabs): number | null => {
            if (getAllRecipientsSizeError) {
                return null;
            }
            switch (tab) {
                case Tabs.ACTIVE:
                    return allRecipientsCount?.active ?? null;
                case Tabs.ALL:
                    return allRecipientsCount?.all ?? null;
                case Tabs.INACTIVE:
                    return allRecipientsCount?.inactive ?? null;
                case Tabs.POTENTIAL:
                    return allRecipientsCount?.potential ?? null;
            }
        },
        [allRecipientsCount, getAllRecipientsSizeError]
    );

    const onTabChange = (event: SyntheticEvent, tab: Tabs) => {
        setActiveTab(tab);
        setQuery({ ...query, recipientsFilter: getFilterForTab(tab), page: 0 });
    };
    const walkmeFeatureFlags = useSelector(store => store.common.walkmeFeatureFlags);
    const shouldRemovePotential = walkmeFeatureFlags?.includes(WalkmeFeatureFlags.CCRemovePotentialRecipients);

    const shownTabs = useMemo(()=>{
      if (!shouldRemovePotential) {
          return AllTabs;
      }
      return AllTabs.filter(tab => tab !== Tabs.POTENTIAL)
    }, [shouldRemovePotential]);
    return (
        <div className={classes.tabList}>
            <TabList
                renderTabLabel={(tab: Tabs) => t(`${tab}`)}
                tabs={shownTabs}
                getTabBadge={getRecipientsCount}
                isTabDisabled={() => isLoading}
                value={activeTab}
                onChange={onTabChange}
                noBorderAndMargins
            />
        </div>
    );
};
